import { forwardRef, type ReactNode, type Ref, type TextareaHTMLAttributes } from 'react';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';
import { TokenFieldCore, type TokenFieldOptions } from './TokenField.js';

export interface TokenTextareaProps
  extends Omit<InputWrapperBaseProps, 'leftSection' | 'rightSection' | 'leftSectionWidth' | 'rightSectionWidth'>,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size' | 'color'>,
    TokenFieldOptions {
  /** Minimum row height. */
  minRows?: number;
  /** Maximum row height before scroll. */
  maxRows?: number;
  autosize?: boolean;
}

/**
 * The multi-line twin of `TokenInput`: a `Textarea` with the same picker and
 * the same chips, for a message body. Wrapping, scrolling and resizing all
 * belong to the native textarea; the painted layer follows it.
 */
export const TokenTextarea = forwardRef(function TokenTextarea(
  props: TokenTextareaProps,
  ref: Ref<HTMLTextAreaElement>
): ReactNode {
  const {
    label, description, error, required, withAsterisk,
    size, className, style,
    id, minRows = 3, maxRows, autosize, rows,
    tokens, trigger, closing, fallbackHint,
    ...ta
  } = props;
  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      withAsterisk={withAsterisk}
      size={size}
      className={className}
      style={style}
      inputId={id}
    >
      {({ inputId, describedBy, invalid, className: ctlClass }) => (
        <TokenFieldCore
          multiline
          tokens={tokens}
          trigger={trigger}
          closing={closing}
          fallbackHint={fallbackHint}
          inputId={inputId}
          describedBy={describedBy}
          invalid={invalid}
          controlClass={ctlClass}
          forwardedRef={ref as Ref<HTMLInputElement | HTMLTextAreaElement>}
          controlProps={{
            rows: rows ?? minRows,
            style: {
              resize: autosize ? 'none' : 'vertical',
              minHeight: autosize ? `calc(${minRows} * 1.5em + 20px)` : undefined,
              maxHeight: maxRows ? `calc(${maxRows} * 1.5em + 20px)` : undefined
            },
            ...ta
          }}
        />
      )}
    </InputWrapper>
  );
});
